"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadNetNamingStrategy = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const NetCombobulator_1 = require("../../../NetCombobulation/NetCombobulator");
const NetNamingRuleChain_1 = require("../../../NetCombobulation/NetNamingRuleChain");
const IndividualGroupingRule_1 = require("../../../NetCombobulation/IndividualGroupingRule");
const FallbackNetNamingRule_1 = require("../../../NetCombobulation/FallbackNetNamingRule");
const OrcadGlobalSignalGroupingRule_1 = require("./rules/OrcadGlobalSignalGroupingRule");
const OrcadHierarchicalPortGroupingRule_1 = require("./rules/OrcadHierarchicalPortGroupingRule");
const OrcadBusMemberGroupingRule_1 = require("./rules/OrcadBusMemberGroupingRule");
const OrcadBusMemberNamingRule_1 = require("./rules/OrcadBusMemberNamingRule");
const OrcadNetScalarGroupingRule_1 = require("./rules/OrcadNetScalarGroupingRule");
const OrcadOffPageConnectorGroupingRule_1 = require("./rules/OrcadOffPageConnectorGroupingRule");
const OrcadGlobalSignalNamingRule_1 = require("./rules/OrcadGlobalSignalNamingRule");
const OrcadOffPageConnectorNamingRule_1 = require("./rules/OrcadOffPageConnectorNamingRule");
const OrcadHierarchicalPortNamingRule_1 = require("./rules/OrcadHierarchicalPortNamingRule");
const OrcadAliasNamingRule_1 = require("./rules/OrcadAliasNamingRule");
const OrcadSystemNameNamingRule_1 = require("./rules/OrcadSystemNameNamingRule");
const OrCadSystemNameSimplifyRule_1 = require("./rules/OrCadSystemNameSimplifyRule");
const OrcadPowerPinNamingRule_1 = require("./rules/OrcadPowerPinNamingRule");
const OrcadWirelessNetSystemNameRule_1 = require("./rules/OrcadWirelessNetSystemNameRule");
const OrcadBusPortPositionalMergeRule_1 = require("./rules/OrcadBusPortPositionalMergeRule");
const OrcadInstanceScopeSuffixRule_1 = require("./rules/OrcadInstanceScopeSuffixRule");
const OrcadAliasCollisionPhysicalNameRule_1 = require("./rules/OrcadAliasCollisionPhysicalNameRule");
const OrcadPowerPortLocalGlobalMergeRule_1 = require("./rules/OrcadPowerPortLocalGlobalMergeRule");
class OrCadNetNamingStrategy {
    static { this.AUTO_NET_PREFIX = 'Net_auto_'; }
    constructor(enableDebug = false, reportHierarchyDiagnostic = () => undefined) {
        this.systemNameRule = new OrcadSystemNameNamingRule_1.OrCadSystemNameNamingRule();
        this.ruleChain = this.buildRuleChain(enableDebug, reportHierarchyDiagnostic);
    }
    get debugRuleChain() {
        return this.ruleChain;
    }
    buildRuleChain(enableDebug = false, reportHierarchyDiagnostic = () => undefined) {
        const chain = new NetNamingRuleChain_1.NetNamingRuleChain(enableDebug);
        chain.add(new IndividualGroupingRule_1.IndividualGroupingRule());
        chain.add(new OrcadNetScalarGroupingRule_1.OrCadNetScalarGroupingRule());
        chain.add(new OrcadPowerPortLocalGlobalMergeRule_1.OrcadPowerPortLocalGlobalMergeRule());
        chain.add(new OrcadBusPortPositionalMergeRule_1.OrcadBusPortPositionalMergeRule());
        chain.add(new OrcadHierarchicalPortGroupingRule_1.OrCadHierarchicalPortGroupingRule(reportHierarchyDiagnostic));
        chain.add(new OrcadBusMemberGroupingRule_1.OrcadBusMemberGroupingRule());
        chain.add(new OrcadGlobalSignalGroupingRule_1.OrCadGlobalSignalGroupingRule());
        chain.add(new OrcadOffPageConnectorGroupingRule_1.OrCadOffPageConnectorGroupingRule());
        chain.add(new OrcadGlobalSignalNamingRule_1.OrCadGlobalSignalNamingRule());
        chain.add(new OrcadOffPageConnectorNamingRule_1.OrCadOffPageConnectorNamingRule());
        chain.add(new OrcadHierarchicalPortNamingRule_1.OrCadHierarchicalPortNamingRule());
        chain.add(new OrcadBusMemberNamingRule_1.OrcadBusMemberNamingRule());
        chain.add(new OrcadAliasNamingRule_1.OrCadAliasNamingRule());
        chain.add(this.systemNameRule);
        chain.add(new OrcadPowerPinNamingRule_1.OrcadPowerPinNamingRule());
        chain.add(new OrcadWirelessNetSystemNameRule_1.OrcadWirelessNetSystemNameRule());
        chain.add(new FallbackNetNamingRule_1.FallbackNetNamingRule('Net_UNKNOWN'));
        chain.add(new OrCadSystemNameSimplifyRule_1.OrCadSystemNameSimplifyRule(this.systemNameRule));
        chain.add(new OrcadInstanceScopeSuffixRule_1.OrcadInstanceScopeSuffixRule());
        chain.add(new OrcadAliasCollisionPhysicalNameRule_1.OrcadAliasCollisionPhysicalNameRule(reportHierarchyDiagnostic));
        return chain;
    }
    transformNetName(netObject, _instance) {
        if (netObject.connection?.connection.appliesNetName &&
            netObject.connection.connection.appliesNetName.labelType !== 'power-pin') {
            return netObject.connection.connection.appliesNetName.label.toUpperCase();
        }
        if (netObject.label) {
            return netObject.label.originalLabel.toUpperCase();
        }
        return undefined;
    }
    canLabelTypeNameNets(labelType, _instance) {
        switch (labelType) {
            case 'alias':
            case 'global':
            case 'off-page-connector':
            case 'hierarchical-port':
            case 'virtual-bus-member':
            case 'power-pin':
                return true;
            case 'hierarchical-sheet-entry':
                return false;
            default:
                return false;
        }
    }
    resolveNetName(net, _instance) {
        for (const connWithPos of net.connections) {
            const applies = connWithPos.connection.appliesNetName;
            if (applies && applies.labelType !== 'power-pin') {
                return applies.label.toUpperCase();
            }
        }
        for (const label of net.labels) {
            if (label.originalLabel && label.canNameNet) {
                return label.originalLabel.toUpperCase();
            }
        }
        const base = net.name.valueOf();
        return base.startsWith(OrCadNetNamingStrategy.AUTO_NET_PREFIX) ? base : (`${OrCadNetNamingStrategy.AUTO_NET_PREFIX}${base}`);
    }
    resolvePhysicalNetNames(hierarchy) {
        return this.ruleChain.execute(hierarchy);
    }
    createVirtualRootHierarchy(roots) {
        if (roots.length === 0) {
            throw new Error('createVirtualRootHierarchy requires at least one root');
        }
        const firstChildInstance = roots[0].instance;
        const virtualInstance = {
            hierarchyEntry: firstChildInstance.hierarchyEntry,
            path: [],
            children: roots.map((r) => r.instance),
            pathUniqueId: '__virtual_root__',
            numInstances: 1,
            totalSheetInstances: 1,
            instanceIndex: 0,
            sheetSymbol: undefined,
            name: '__virtual_root__',
            designator: '',
            netRefCtx: new sigma_engine_shared_1.NetRefCtx(),
            isMultiInstanceSheet: false,
            instOccurence: firstChildInstance.instOccurence,
        };
        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(virtualInstance.netRefCtx, this, virtualInstance, {
            autoAssignNets: false,
            pointRoundingPrecision: 0.1,
        });
        return {
            pathUniqueId: '__virtual_root__',
            hierarchyDepth: -1,
            instance: virtualInstance,
            combobulator: emptyCombobulator,
            children: roots,
        };
    }
}
exports.OrCadNetNamingStrategy = OrCadNetNamingStrategy;
//# sourceMappingURL=OrcadNetNamingStrategy.js.map